import { arrayOf, bool, shape, string, func, number } from 'prop-types';
import React, { Component, createRef } from 'react';
import classnames from 'classnames';
import throttle from 'lodash.throttle';
import JWVideo from 'components/JWVideo';
import VideoPlaylist from 'components/VideoPlaylist';
import LazyLoad from 'components/LazyLoad';
import Link from 'components/Link';
import Arrow from 'components/Icons/Arrow';
import Comscore from 'components/ComscoreStreamingAnalytics';
import Prebid from 'components/Prebid';
import AmazonTam from 'components/AmazonTam';
import Permutive from 'components/Permutive';
import { SITE_NAME } from 'constants/site';
import { hasTermWithSlug } from 'util/terms';
import { getIsoDate } from 'oxygen-lib-ui/helpers/date';
import { JW_HIDE_ADS_TAG, getTags, getAdTag, getCustParams } from 'util/video';
import { getViewportMode } from 'util/viewport';
import jwEventHandlers from 'components/ComscoreStreamingAnalytics/jwEventHandlers';

import {
  JW_PLAYER_CONFIG,
  JW_PLAYER_DESKTOP_BREAKPOINT,
  JW_PLAYER_RELATED_PLAYLIST,
  JW_AD_SCHEDULE_ID,
  VIDEO_HUB_URL
} from 'constants/video';

const PLACEHOLDER = (
  <div className="JWVideo">
    <div>
      <div
        className="jwplayer"
        style={{ paddingBottom: '56.25%', background: '#000' }}
      />
    </div>
  </div>
);

const SCROLL_HANDLER_THROTTLE = 100; // ms
const JW_PLAYING_STATE = 'playing';

const MINIMIZED_VIDEO_PADDING = 10; // px; matches left position in CSS
const DOCKED_VIDEO_HEIGHT = 140;

// CLEANUP: some better class names perhaps?
const CLASS_MINIMIZED = 'is-minimized';
const CLASS_SCROLL_UP = 'is-at-post-bottom';
const CLASS_NORMAL = 'is-normal';
const CLASS_ARTICLE_VIDEO = 'Post--with-minimized-video';

export const getConfig = (placement, device = 'mobile') =>
  JW_PLAYER_CONFIG[`${placement}:${device}`] ||
  JW_PLAYER_CONFIG[placement] ||
  JW_PLAYER_CONFIG.default;

class Video extends Component {
  constructor(props) {
    super(props);

    const { placement, showTitle } = props;

    this.state = {
      displayTitle: placement === 'right_rail' ? false : showTitle,
      video: undefined,
      playerTopPos: null, // px
      playerBottomPos: null, // px
      extraClasses: [],
      playOnViewable: false,
      // Has the player ever attempted to play ads or video?
      hasStartedPlayback: false,
      isPlayerMinimized: false,
      playlistItems: null,
      selectedThumbIdx: 0
    };

    this.onReady = this.onReady.bind(this);
    this.onTime = this.onTime.bind(this);
    this.onVideoLoad = this.onVideoLoad.bind(this);
    this.onBeforePlay = this.onBeforePlay.bind(this);
    this.onViewable = this.onViewable.bind(this);
    this.track = this.track.bind(this);
    this.addScrollListeners = this.addScrollListeners.bind(this);
    this.removeScrollListeners = this.removeScrollListeners.bind(this);
    this.handleUnminimize = this.handleUnminimize.bind(this);

    this.ref = createRef();

    // JW player object
    // https://developer.jwplayer.com/jw-player/docs/javascript-api-reference
    this.player = null;

    // Get the custom config for this placement. Note, some placements have mobile
    // and desktop configs.
    this.placementConfig = getConfig(
      placement,
      getViewportMode(JW_PLAYER_DESKTOP_BREAKPOINT)
    );

    // only activate the minimizing behavior if the placement includes a `sticky`
    // config value
    if (this.placementConfig.sticky) {
      this._scrollHandler = throttle(
        this.scrollHandler.bind(this),
        SCROLL_HANDLER_THROTTLE
      );
    }
  }

  componentDidMount() {
    if (this._scrollHandler) {
      this.addScrollListeners();
      // call positionPlayer() on mount so if we reload when in the middle of
      // the page, the player is immediately visible without scrolling.
      if (this.ref.current) {
        this.positionPlayer();
      }
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.isPlayerMinimized !== this.state.isPlayerMinimized) {
      // Player size has changed. Call resize to update the player UI so it is
      // appropriate for the new size (e.g. number of items in the related grid)
      this.player.resize('100%');
    }
  }

  componentWillUnmount() {
    if (this._scrollHandler) {
      this.removeScrollListeners();
    }
  }

  onReady(_, player) {
    // get the video player instance so we can programmatically do stuff later
    // without an event being fired
    this.player = this.player || player;

    const { showPlaylist } = this.props;
    if (showPlaylist) {
      const playlistItems = player.getPlaylist();
      this.setState({ playlistItems });
    }
  }

  onVideoLoad(video) {
    this.setState({ video, selectedThumbIdx: this.player.getPlaylistIndex() });
  }

  onTime(e) {
    const { showTitle } = this.props;
    const { displayTitle } = this.state;
    if (e.currentTime > 0 && displayTitle !== showTitle) {
      this.setState({ displayTitle: showTitle });
    }
  }

  onBeforePlay(e, player) {
    const { placement, hideAds, post } = this.props;

    // Set hasStartedPlayback on the first beforePlay event. This is the best
    // way to know if a player has attempted playback since it is called before
    // the `adPlay` and `play` events.
    if (this.state.hasStartedPlayback === false) {
      this.setState({
        hasStartedPlayback: true
      });
    }

    if (hideAds) {
      // Ads are disabled for this video
      return;
    }

    if (player.getCurrentTime() === 0) {
      const tags = getTags(player.getPlaylistItem());
      if (tags.includes(JW_HIDE_ADS_TAG)) {
        // This video has been tagged with "hide ads"
        // Don't return anything
        return;
      }
      const playerId = player.getContainer().id;
      if (
        window.pbjs &&
        window.pbjs.adUnits &&
        window.pbjs.adUnits.find(unit => unit.code === playerId)
      ) {
        Promise.all([
          Prebid.fetchVideoBid(playerId, getCustParams(placement, post)),
          AmazonTam.fetchVideoBid(placement)
        ]).then(([pbjsVastUrl, tamEncodedQsParams]) => {
          // Combine pbjs and tam bid params into single vast url
          const videoUrlWithParams = `${pbjsVastUrl}&scp=${tamEncodedQsParams}`;
          player.playAd(videoUrlWithParams);
        });
      } else {
        player.playAd(getAdTag(placement, post));
      }
      if (placement === 'right_rail') {
        this.setState({ displayTitle: false });
      }
    }
  }

  onViewable(e, player) {
    if (!player) {
      return;
    }

    if (e.viewable === 0 && player.getState() === JW_PLAYING_STATE) {
      // Player is out of view. Pause it if it is playing.
      player.pause();
      this.setState({
        playOnViewable: true
      });
    }

    if (e.viewable && this.state.playOnViewable) {
      // Restart auto-paused player now that it is in view.
      player.play();
      this.setState({
        playOnViewable: false
      });
    }
  }

  getPositions() {
    // CLEANUP: is there a better way to do this?
    const el = this.ref.current;
    const playerEl = this.ref.current.querySelector('.Video__player');
    const articleEl = el.closest('article');
    const articleOffsetTop = articleEl.offsetTop;

    const {
      top: containerTop,
      width: containerWidth
    } = el.getBoundingClientRect();

    const {
      height: articleHeight,
      top: articleTop
    } = articleEl.getBoundingClientRect();

    const viewportHeight = window.innerHeight;

    const {
      top: playerTop,
      height: playerHeight
    } = playerEl.getBoundingClientRect();

    return {
      viewportHeight,
      containerTop,
      containerWidth,
      articleHeight,
      articleTop,
      articleOffsetTop,
      playerTop,
      playerHeight
    };
  }

  track(e, player) {
    const { post, placement } = this.props;
    if (placement !== 'in_post') {
      // Only track interaction with the in post player in Permutive
      return;
    }
    const video = player.getPlaylistItem();
    const tags = getTags(video);
    if (player.getPosition() === 0) {
      Permutive.track('VideoPlay', {
        video: {
          title: video.title,
          tags,
          length: video.duration
        },
        article: {
          authors: [post.author.name],
          tags: post.tags.map(c => c.slug),
          categories: post.categories.map(c => c.slug)
        }
      });
    }
  }

  addScrollListeners() {
    window.addEventListener('scroll', this._scrollHandler, false);
    window.addEventListener('resize', this._scrollHandler, false);
  }

  removeScrollListeners() {
    window.removeEventListener('scroll', this._scrollHandler, false);
    window.removeEventListener('resize', this._scrollHandler, false);
  }

  scrollHandler() {
    this.positionPlayer();
  }

  positionPlayer() {
    if (this.state.hasStartedPlayback === false) {
      // Only position the player if it has begun playback
      return;
    }

    const anchor = this.placementConfig.sticky || 'bottom';

    const { setExtraArticleClasses } = this.props;

    const {
      viewportHeight,
      containerTop,
      containerWidth,
      articleHeight,
      articleTop,
      articleOffsetTop,
      playerHeight
    } = this.getPositions();

    // Minimize the player when it reaches the top of the screen
    const playerShouldMinimize = containerTop <= 0;

    // Should we switch from sticking to the bottom to scrolling up?
    let shouldScrollUp = false;

    if (anchor === 'top') {
      shouldScrollUp =
        articleHeight + articleTop - MINIMIZED_VIDEO_PADDING <
        DOCKED_VIDEO_HEIGHT;
    }
    if (anchor === 'bottom') {
      shouldScrollUp =
        articleHeight + articleTop - MINIMIZED_VIDEO_PADDING < viewportHeight;
    }

    // Compute appropriate CSS class to provide styling hooks for minimized view
    let extraClasses = [CLASS_NORMAL];

    if (playerShouldMinimize) {
      extraClasses = [CLASS_MINIMIZED, `${CLASS_MINIMIZED}--${anchor}`];
    }

    if (shouldScrollUp) {
      extraClasses = [CLASS_SCROLL_UP, `${CLASS_SCROLL_UP}--${anchor}`];
    }

    setExtraArticleClasses(
      playerShouldMinimize ? [CLASS_ARTICLE_VIDEO] : undefined
    );

    // Compute the top/bottom player position when it's minimized
    let playerTopPos = null;
    let playerBottomPos = null;
    let playerWidth = null;

    if (playerShouldMinimize) {
      // prettier-ignore
      const scrollUpTop = `${Math.round(articleHeight - playerHeight - MINIMIZED_VIDEO_PADDING + articleOffsetTop)}px`;

      if (anchor === 'top') {
        if (shouldScrollUp) {
          playerTopPos = scrollUpTop;
          playerWidth = null;
        } else {
          playerTopPos = '0px';
          playerWidth = containerWidth;
        }
      } else if (anchor === 'bottom') {
        if (shouldScrollUp) {
          playerTopPos = scrollUpTop;
          playerBottomPos = null;
        } else {
          playerTopPos = null;
          playerBottomPos = `${MINIMIZED_VIDEO_PADDING}px`;
        }
      }
    }

    // update our elements
    this.setState({
      isPlayerMinimized: playerShouldMinimize,
      extraClasses,
      playerTopPos,
      playerBottomPos,
      playerWidth
    });
  }

  restoreOriginalPlayerPosition() {
    this.setState({
      extraClasses: [CLASS_NORMAL],
      playerTopPos: null,
      playerBottomPos: null
    });
  }

  handleUnminimize() {
    this.restoreOriginalPlayerPosition();
    // Remove listeners so the video does not stick again
    this.removeScrollListeners();
  }

  render() {
    const {
      videoId,
      playlist,
      placement,
      title,
      description,
      duration,
      canonical,
      poster,
      publishedAt,
      post,
      showPlaylist
    } = this.props;
    const {
      playerTopPos,
      playerBottomPos,
      playerWidth,
      extraClasses,
      video,
      displayTitle,
      playlistItems,
      selectedThumbIdx
    } = this.state;

    const customEventHandlers = JWVideo.aggregateEvents(jwEventHandlers, {
      // Set the current video so we can access metadata (e.g. title)
      onVideoLoad: this.onVideoLoad,
      onBeforePlay: this.onBeforePlay,
      onViewable: this.onViewable,
      onTime: this.onTime,
      onReady: this.onReady,
      // Permutive tracking
      onPlay: this.track,
      onAutoStart: this.track,
      onResume: this.track
    });

    let category;
    // CLEANUP: verify this logic
    if (placement === 'in_post' && post && post.categories) {
      // use primary category, of which there should only be one
      [category] = post.categories.map(c => c.name);
    } else {
      category = placement;
    }

    // only set specific props when they are not null
    const playerStyle = {
      ...(playerTopPos === null ? undefined : { top: playerTopPos }),
      ...(playerBottomPos === null ? undefined : { bottom: playerBottomPos }),
      ...(playerWidth === null ? undefined : { width: playerWidth })
    };

    const wrapperClasses = classnames('Video', ...extraClasses, {
      [`Video--${placement}`]: placement
    });

    const isMinimizable = !!this.placementConfig.sticky;

    // Show related videos if it is enabled for the placement and action-no-playlist
    // has NOT been set on the post
    const showRelatedVideos =
      this.placementConfig.showRelatedVideos &&
      (!post || !hasTermWithSlug(post.tags, 'action-no-playlist'));

    const calloutEl = {
      in_post: <a>Watch</a>,
      right_rail: (
        <a>
          Watch More {SITE_NAME} Videos{' '}
          <span className="Video__callout-icon">
            <Arrow />
          </span>
        </a>
      )
    };

    return (
      <div className={wrapperClasses} ref={this.ref}>
        {(placement === 'in_post' || placement === 'right_rail') && (
          <div className="Video__callout">
            <Link href={VIDEO_HUB_URL}>{calloutEl[placement]}</Link>
          </div>
        )}
        {displayTitle && (title || this.state.video) && (
          <div className="Video__title">
            {video && video.item && video.item.title ? video.item.title : title}
          </div>
        )}

        {!!videoId && !!title && !!poster && !!publishedAt && (
          <script
            className="json-ld"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'VideoObject',
                uploadDate: getIsoDate(publishedAt),
                name: title,
                description: description || 'AT Media video',
                duration: `PT${duration}S`,
                thumbnailUrl: poster,
                contentUrl: `https://cdn.jwplayer.com/manifests/${videoId}.m3u8`,
                ...(canonical ? { embedUrl: canonical } : undefined)
              })
            }}
          />
        )}

        <LazyLoad placeholder={PLACEHOLDER} once>
          <Comscore />
          <div className="Video__player" style={playerStyle}>
            <JWVideo
              playerScript="/static/jwplayer.8.17.3.js"
              licenseKey={process.env.JW_KEY}
              videoId={videoId}
              playlist={playlist}
              placement={placement}
              category={category}
              customProps={{
                ...this.placementConfig.props,
                // Allow autostart to be overridden with post tag
                ...(post && hasTermWithSlug(post.tags, 'action-no-autoplay')
                  ? {
                      autostart: false,
                      mute: false
                    }
                  : {}),
                ...{
                  // Add shared advertising configuration
                  // Note, react-jw-player has a `generatePrerollUrl` prop, but
                  // this does not support adding an `adscheduleid`
                  // `playAd` is called in `this.onBeforePlay` if ads are enabled
                  advertising: {
                    client: 'googima',
                    admessage: 'Ad — xxs left',
                    autoplayadsmuted: true,
                    adscheduleid:
                      JW_AD_SCHEDULE_ID[placement] || JW_AD_SCHEDULE_ID.default
                  }
                },
                ...(showRelatedVideos
                  ? {
                      related: JW_PLAYER_RELATED_PLAYLIST
                    }
                  : {})
              }}
              customEventHandlers={customEventHandlers}
            />
            {showPlaylist && playlistItems && (
              <VideoPlaylist
                playlistItems={playlistItems}
                currentVideoTitle={playlistItems[selectedThumbIdx].title}
                selected={selectedThumbIdx}
                onPlayVideo={key =>
                  this.setState({ selectedThumbIdx: parseInt(key, 10) }, () =>
                    this.player.playlistItem(key)
                  )
                }
              />
            )}
            {isMinimizable && (
              <button
                className="Video__unminimize"
                onClick={this.handleUnminimize}
                aria-label="close"
                data-gtm-video-id={videoId || playlist}
                data-gtm-video-player-name={this.placementConfig.playerName}
              >
                X
              </button>
            )}
          </div>
        </LazyLoad>
      </div>
    );
  }
}

Video.defaultProps = {
  videoId: undefined,
  playlist: undefined,
  placement: 'default',
  showTitle: false,
  post: undefined,
  setExtraArticleClasses: () => {},
  title: undefined,
  description: undefined,
  duration: undefined,
  canonical: undefined,
  poster: undefined,
  publishedAt: undefined,
  hideAds: false,
  showPlaylist: false
};

Video.propTypes = {
  videoId: string,
  playlist: string,
  placement: string,
  showTitle: bool,
  title: string,
  description: string,
  duration: string,
  canonical: string,
  poster: string,
  publishedAt: number,
  post: shape({
    categories: arrayOf(
      shape({
        name: string,
        slug: string
      })
    ),
    tags: arrayOf(
      shape({
        name: string,
        slug: string
      })
    )
  }),
  setExtraArticleClasses: func,
  hideAds: bool,
  showPlaylist: bool
};

export default Video;
